"use client";

import { motion } from "motion/react";
import { type ReactNode } from "react";

interface ImageHeroContentProps {
  title: string;
  subtitle: string;
  children: ReactNode;
}

/**
 * Client component for animated hero content.
 * Bold, brutalist typography with cinematic entrance animations.
 */
export const ImageHeroContent = ({
  title,
  subtitle,
  children,
}: ImageHeroContentProps) => {
  // Split title into words for staggered animation
  const titleWords = title.split(" ");

  return (
    <div className="relative z-10 flex h-full flex-col">
      {/* Main content - positioned at bottom */}
      <div className="mt-auto px-6 pb-10 md:px-12 md:pb-12 lg:pb-16">
        {/* Accent line above title */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="mb-6 h-1 w-16 origin-left bg-strategy-gold md:mb-8 md:w-20"
        />

        {/* Title - brutalist, word-by-word animation */}
        <h1 className="mb-4 md:mb-6">
          {titleWords.map((word, index) => (
            <motion.span
              key={index}
              initial={{ opacity: 0, y: 60, rotateX: -45 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.1 + index * 0.08,
                ease: [0.25, 0.1, 0.25, 1],
              }}
              className="mr-3 inline-block font-heading text-5xl font-black uppercase leading-[0.9] tracking-tight text-freedom sm:text-6xl md:mr-4 md:text-7xl lg:text-8xl xl:text-9xl"
            >
              {word}
            </motion.span>
          ))}
        </h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          className="max-w-xl text-base leading-relaxed text-freedom/80 md:text-lg lg:max-w-2xl lg:text-xl"
        >
          {subtitle}
        </motion.p>

        {/* CTA buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
          className="mt-8 flex flex-col gap-4 sm:flex-row md:mt-10"
        >
          {children}
        </motion.div>
      </div>
    </div>
  );
};
